import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ScheduledMessageStatus } from '@shared/scheduled-message-status';

const states: Record<string, { label: string; guidance: string; className: string }> = {
  pending: {
    label: 'Pending',
    guidance: 'Waiting to send. You can still cancel.',
    className: 'text-muted-foreground',
  },
  processing: {
    label: 'Processing — delivery unconfirmed',
    guidance: 'Delivery has begun and cannot be cancelled. If this status persists, processing may have been interrupted. Check the chat before sending again; this message will not be retried automatically.',
    className: 'text-amber-600 dark:text-amber-400',
  },
  failed: {
    label: 'Failed — delivery unconfirmed',
    guidance: 'The send attempt did not finish successfully, but the message may already be in the chat. Check the chat before sending again. No automatic retry; cancellation is unavailable.',
    className: 'text-destructive',
  },
  sent: {
    label: 'Sent',
    guidance: 'Sent to the chat.',
    className: 'text-muted-foreground',
  },
};

function timestamp(value: string | null) {
  if (!value || Number.isNaN(Date.parse(value))) return 'Not recorded';
  return new Date(value).toLocaleString('de-DE', {
    dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Berlin',
  }) + ' (Berlin)';
}

export function ScheduledMessageList({ messages, onCancel, isCancelling, cancellationError }: {
  messages: ScheduledMessageStatus[];
  onCancel: (id: number) => void;
  isCancelling: boolean;
  cancellationError?: string;
}) {
  return (
    <>
      <div className="text-xs font-medium text-muted-foreground mb-2">Scheduled messages</div>
      {cancellationError && <p role="alert" className="text-xs text-destructive mb-2">{cancellationError}</p>}
      <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
        {messages.map(message => {
          const state = states[message.status] ?? {
            label: 'Delivery status unknown',
            guidance: 'Check the chat before sending again. Cancellation is unavailable.',
            className: 'text-destructive',
          };
          return (
            <div key={message.id} className="flex items-start gap-2 p-1.5 rounded bg-muted/50">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] truncate">{message.content?.replace(/<[^>]*>/g, '').slice(0, 60) || 'Attachment message'}</div>
                <div className={`text-[11px] font-medium mt-0.5 ${state.className}`}>{state.label}</div>
                <div className="text-[10px] text-muted-foreground">
                  <div>Created: {timestamp(message.createdAt)}</div>
                  <div>Scheduled: {timestamp(message.scheduledFor)}</div>
                  {message.status === 'sent' && <div>Sent: {timestamp(message.completedAt)}</div>}
                  {message.status === 'failed' && <div>Attempt ended: {timestamp(message.completedAt)}</div>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{state.guidance}</p>
              </div>
              {message.status === 'pending' && (
                <Button variant="ghost" size="icon"
                  className="h-5 w-5 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => onCancel(message.id)} disabled={isCancelling}
                  title="Cancel pending message" aria-label="Cancel pending message">
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
